/**
 * Parser OFX para os formatos OFX 1.x (SGML) e OFX 2.x (XML).
 * Retorna um objeto com:
 *   - resumo: { bankId, acctId, dtStart, dtEnd, ledgerBal }
 *   - transacoes: Array<{ fitId, dtPosted, trnAmt, trnType, memo }>
 */
export function parseOFX(content) {
  const text = content.trim();

  // OFX 2.x começa com declaração XML ou tag <OFX>
  if (text.startsWith("<?xml") || text.startsWith("<OFX>") || text.startsWith("<ofx>")) {
    return parseOFXXml(text);
  }

  // OFX 1.x: headers de texto seguidos de bloco SGML
  return parseOFXSgml(text);
}

// ─── OFX 1.x SGML ────────────────────────────────────────────────────────────

function parseOFXSgml(text) {
  // Remove cabeçalhos (linhas antes de <OFX>)
  const ofxStart = text.search(/<OFX>/i);
  const body = ofxStart >= 0 ? text.slice(ofxStart) : text;

  const getTag = (tagName, src) => {
    const re = new RegExp(`<${tagName}>([^<]*)`, "i");
    const m = src.match(re);
    return m ? m[1].trim() : null;
  };

  const resumo = {
    bankId: getTag("BANKID", body),
    acctId: getTag("ACCTID", body),
    dtStart: formatOFXDate(getTag("DTSTART", body)),
    dtEnd: formatOFXDate(getTag("DTEND", body)),
    ledgerBal: parseFloat(getTag("BALAMT", body) || "0"),
  };

  // Extrai cada bloco <STMTTRN>...</STMTTRN>
  const transacoes = [];
  const trnRe = /<STMTTRN>([\s\S]*?)<\/STMTTRN>/gi;
  let match;
  while ((match = trnRe.exec(body)) !== null) {
    const block = match[1];
    transacoes.push({
      fitId: getTag("FITID", block) || "",
      dtPosted: getTag("DTPOSTED", block) || "",
      trnAmt: parseFloat((getTag("TRNAMT", block) || "0").replace(",", ".")),
      trnType: (getTag("TRNTYPE", block) || "OTHER").toUpperCase(),
      memo: getTag("MEMO", block) || getTag("NAME", block) || "",
    });
  }

  return { resumo, transacoes };
}

// ─── OFX 2.x XML ─────────────────────────────────────────────────────────────

function parseOFXXml(text) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "application/xml");

  const getText = (tagName) => {
    const el = doc.getElementsByTagName(tagName)[0];
    return el ? el.textContent.trim() : null;
  };

  const resumo = {
    bankId: getText("BANKID"),
    acctId: getText("ACCTID"),
    dtStart: formatOFXDate(getText("DTSTART")),
    dtEnd: formatOFXDate(getText("DTEND")),
    ledgerBal: parseFloat(getText("BALAMT") || "0"),
  };

  const transacoes = [];
  const trnElements = doc.getElementsByTagName("STMTTRN");
  for (let i = 0; i < trnElements.length; i++) {
    const el = trnElements[i];
    const getChildText = (name) => {
      const child = el.getElementsByTagName(name)[0];
      return child ? child.textContent.trim() : null;
    };
    transacoes.push({
      fitId: getChildText("FITID") || "",
      dtPosted: getChildText("DTPOSTED") || "",
      trnAmt: parseFloat((getChildText("TRNAMT") || "0").replace(",", ".")),
      trnType: (getChildText("TRNTYPE") || "OTHER").toUpperCase(),
      memo: getChildText("MEMO") || getChildText("NAME") || "",
    });
  }

  return { resumo, transacoes };
}

// ─── Utilidades ──────────────────────────────────────────────────────────────

/**
 * Converte datas OFX para "YYYY-MM-DD":
 *   - "20231001000000" ou "20231001" → "2023-10-01"
 *   - "2023-10-01T00:00:00Z" → "2023-10-01"
 *   - "2023-10-01" → "2023-10-01"
 */
function formatOFXDate(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  
  // Tenta padrão ISO: 2026-01-01 ou 2026-01-01T00:00:00Z
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }
  
  // Tenta padrão OFX tradicional: YYYYMMDD...
  const digitos = s.replace(/\D/g, "");
  if (digitos.length >= 8) {
    return `${digitos.slice(0, 4)}-${digitos.slice(4, 6)}-${digitos.slice(6, 8)}`;
  }
  
  return raw;
}
